import React, { useEffect, useState } from "react";
import { Route, Routes, Navigate, Link, useLocation } from "react-router-dom";
import {
  MaintenanceProvider,
  MaintenanceGuard,
} from "./contexts/MaintenanceContext";
import "./styles/MaintenanceMode.css";
import { captureFacebookAdParams } from "./utils/adTracking";
import {
  FiFileText,
  FiLogOut,
  FiMenu,
  FiSettings,
  FiUser,
  FiChevronDown,
  FiChevronRight,
  FiMessageCircle, // Thêm import cho icon mới
  FiShield, // Thêm icon cho Admin
  FiUsers, // Thêm icon cho quản lý người dùng
  FiActivity, // Thêm icon cho quản lý lỗi
  FiDatabase, // Thêm icon cho quản lý dữ liệu
  FiMessageSquare, // Icon for Feedback
  FiBookmark,
  FiDollarSign, // Added FiDollarSign import
  FiCreditCard, // Added FiCreditCard import for Payment Test
  FiHome, // Added FiHome import
  FiKey, // Added FiKey import for API Keys management
} from "react-icons/fi"; // Add new icons
import LoginGoogle from "./LoginGoogle";
import { ref, onValue } from "firebase/database"; // Import Firebase database functions
import { getAuth } from "firebase/auth"; // Import Firebase Auth
import ProfileScreen from "./ProfileScreen";
import PricingScreen from "./components/PricingScreen";
import { db } from "./firebase/config";
import PolicyScreen from "./PolicyScreen"; // Thêm dòng này ở phần import
import TermsScreen from "./TermsScreen"; // Thêm dòng này ở phần import
import NewsScreen from "./NewsScreen";
import { HelmetProvider } from "react-helmet-async";
import NewsDetailScreen from "./NewsDetailScreen";
import AdminNewsGenerator from "./AdminNewsGenerator"; // Import AdminNewsGenerator
import MetaPixelPageTracker from "./components/MetaPixelPageTracker"; // Import Meta Pixel tracker
import { logEvent } from "firebase/analytics";
import { analytics } from "./firebase/config";
import LanguageSelector from "./components/LanguageSelector"; // Import LanguageSelector thay vì GoogleTranslate
import AdminDashboard from "./AdminDashboard"; // Import AdminDashboard
import AdminUserManagement from "./AdminUserManagement"; // Import AdminUserManagement
import AdminErrorManagement from "./AdminErrorManagement"; // Import AdminErrorManagement
import AdminUsageManagement from "./AdminUsageManagement"; // Import AdminUsageManagement
import AdminUsageAnalytics from "./AdminUsageAnalytics"; // Import AdminUsageAnalytics
import AdminManusKeyManager from "./AdminManusKeyManager"; // Import AdminManusKeyManager

import FeedbackForm from "./components/FeedbackForm"; // Import FeedbackForm
import { Outlet } from "react-router-dom"; // Import Outlet
import LandingPage2 from "./LandingPage2";
import AIPrice from "./components/AIPrice/AIPrice"; // Import AIPrice component
import AIRentalPrice from "./components/AIRentalPrice"; // Import AIRentalPrice component
import LandingHeader from "./components/LandingHeader"; // Import LandingHeader component
import PriceHistoryScreen from "./components/PriceHistoryScreen"; // Import PriceHistoryScreen component
import PaymentSuccess from "./components/PaymentSuccess"; // Import PaymentSuccess component
import PaymentCancel from "./components/PaymentCancel"; // Import PaymentCancel component
import PaymentFlowTest from "./components/PaymentFlowTest"; // Import PaymentFlowTest component

// Layout component using MaintenanceGuard
const MaintenanceLayout = () => {
  return (
    <MaintenanceGuard>
      <Outlet />
    </MaintenanceGuard>
  );
};

const AppContent = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(
    !!localStorage.getItem("userAccessToken") &&
      !!localStorage.getItem("userId")
  );
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false); // State to handle sidebar collapse
  const [expandedGroups, setExpandedGroups] = useState({
    posts: true,
    settings: true,
    admin: true,
  });
  const location = useLocation();
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);

  // Thêm tracking cho page views và bắt tham số quảng cáo Facebook
  useEffect(() => {
    try {
      // Bắt và lưu tham số quảng cáo Facebook
      captureFacebookAdParams();

      // Track page view với kiểm tra analytics có sẵn
      if (analytics) {
        logEvent(analytics, "page_view", {
          page_path: location.pathname,
          page_title: document.title,
        });
      }
    } catch (error) {
      console.warn("Analytics tracking error:", error);
      // Không throw error để tránh crash app
    }
  }, [location]);

  useEffect(() => {
    const handleStorageChange = () => {
      setIsLoggedIn(
        !!localStorage.getItem("userAccessToken") &&
          !!localStorage.getItem("userId")
      );
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  useEffect(() => {
    if (isLoggedIn) {
      const userId = localStorage.getItem("userId");
      if (!userId) {
        // Nếu không có userId nhưng vẫn được đánh dấu là đã đăng nhập,
        // có thể đã xảy ra lỗi trạng thái - đăng xuất
        handleLogout();
        return;
      }

      const userDataRef = ref(db, `/users/${userId}`);
      onValue(userDataRef, (snapshot) => {
        const userData = snapshot.val();
        if (!userData) return;

        // Xử lý fanpages
        const fanpagesData = userData?.fanpages;
        if (fanpagesData) {
          localStorage.setItem("fanpages", JSON.stringify(fanpagesData));
        } else {
          localStorage.removeItem("fanpages");
        }

        // Kiểm tra và thông báo khi có points > 0 nhưng không có membership
        const hasMembership = userData?.membership !== undefined;
        const pointsData = userData?.points;
        const currentPoints =
          pointsData?.points !== undefined
            ? pointsData.points + (pointsData.purchasedPoints || 0)
            : typeof pointsData === "number"
            ? pointsData
            : 0;

        if (currentPoints > 0 && !hasMembership) {
          sessionStorage.setItem("showMembershipWarning", "true");
        } else {
          sessionStorage.removeItem("showMembershipWarning");
        }
      });
    }
  }, [isLoggedIn]);

  const handleLogout = () => {
    // Track logout event
    try {
      if (analytics) {
        logEvent(analytics, "logout");
      }
    } catch (error) {
      console.warn("Analytics logout tracking error:", error);
      // Không throw error để tránh crash app
    }

    // Đăng xuất khỏi Firebase Authentication
    try {
      const auth = getAuth();
      auth
        .signOut()
        .then(() => {
          console.log("Đăng xuất Firebase Auth thành công");
        })
        .catch((error) => {
          console.error("Lỗi khi đăng xuất Firebase Auth:", error);
        });
    } catch (firebaseError) {
      console.error("Lỗi Firebase Auth:", firebaseError);
    }

    // Xóa dữ liệu trong localStorage
    localStorage.removeItem("userAccessToken");
    localStorage.removeItem("userId");
    localStorage.removeItem("fanpages");
    localStorage.removeItem("returnUrl");
    localStorage.removeItem("userAccessTokenExpiry");
    localStorage.removeItem("isAdmin"); // Xóa trạng thái admin
    localStorage.removeItem("adminAuthenticated"); // Xóa xác thực admin
    // Tùy chọn: có thể giữ nguyên ngôn ngữ người dùng đã chọn
    // localStorage.removeItem("selectedLanguageCode");
    // localStorage.removeItem("selectedLanguageDisplayName");

    // Dọn dẹp sessionStorage nếu cần
    sessionStorage.removeItem("showMembershipWarning");

    setIsLoggedIn(false);
  };

  const toggleGroup = (groupName) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupName]: !prev[groupName],
    }));
  };

  // DEPRECATED: Old membership warning - not used in free model
  // const renderMembershipWarning = () => {
  //   return null;
  // };

  // First check if we're on the landing page, pricing page, or news page
  if (
    location.pathname === "/" ||
    location.pathname === "/landing-page" ||
    location.pathname === "/pricing" ||
    location.pathname === "/policy" ||
    location.pathname === "/terms" ||
    location.pathname === "/news" ||
    location.pathname.startsWith("/news/") ||
    location.pathname === "/admin-news" ||
    location.pathname === "/admin-dashboard" ||
    location.pathname === "/admin-users" ||
    location.pathname === "/admin-errors" ||
    location.pathname === "/admin-usage-management" ||
    location.pathname === "/admin-usage-analytics" ||
    location.pathname === "/admin-manus-keys"
  ) {
    if (location.pathname === "/" || location.pathname === "/landing-page") {
      return <LandingPage2 />;
    }
    if (location.pathname === "/policy") {
      return <PolicyScreen />;
    }
    if (location.pathname === "/terms") {
      return <TermsScreen />;
    }
    if (location.pathname === "/admin-news") {
      return <AdminNewsGenerator />;
    }
    if (location.pathname === "/admin-dashboard") {
      return <AdminDashboard />;
    }
    if (location.pathname === "/admin-users") {
      return <AdminUserManagement />;
    }
    if (location.pathname === "/admin-errors") {
      return <AdminErrorManagement />;
    }
    if (location.pathname === "/admin-usage-management") {
      return <AdminUsageManagement />;
    }
    if (location.pathname === "/admin-usage-analytics") {
      return <AdminUsageAnalytics />;
    }
    if (location.pathname === "/admin-manus-keys") {
      return <AdminManusKeyManager />;
    }
    if (location.pathname === "/news" || location.pathname.includes("/news/")) {
      return (
        <>
          <LandingHeader />
          <Routes>
            <Route path="/news" element={<NewsScreen />} />
            <Route path="/news/:slug" element={<NewsDetailScreen />} />
          </Routes>
        </>
      );
    }
    return <PricingScreen />;
  }

  return (
    <div className="flex min-h-screen bg-gray-100">
      {" "}
      {/* Added min-h-screen */}
      {isLoggedIn ? (
        <>
          {/* Overlay for mobile */}
          {isSidebarOpen && (
            <div
              className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-10"
              onClick={() => setIsSidebarOpen(false)}
            />
          )}

          {/* Mobile Header */}
          <header className="md:hidden fixed top-0 left-0 right-0 h-12 bg-gray-700 shadow-md z-30 flex items-center px-4">
            <button
              className="text-white mr-4"
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            >
              <FiMenu className="text-xl" /> {/* Reduced icon size */}
            </button>
            <div className="flex items-center">
              <img
                src={require("./assets/images/aiprice-logo.png")}
                alt="Logo"
                className="h-10" /* Increased logo height */
              />
              <span className="text-white font-bold text-xl ml-2">AIPrice</span>
            </div>
          </header>

          {/* Sidebar Navigation */}
          <nav
            className={`bg-gray-800 flex flex-col fixed md:sticky top-0 h-screen z-20 transition-transform duration-300 ${
              isSidebarOpen ? "translate-x-0" : "-translate-x-full"
            } md:translate-x-0 md:static md:mt-0 mt-12 ${
              isSidebarCollapsed ? "w-28" : "w-72"
            }`}
          >
            {/* Container cho toàn bộ nội dung có thể scroll */}
            <div className="flex flex-col h-full">
              {/* Phần có thể scroll */}
              <div className="flex-1 overflow-y-auto p-4 pb-24 md:pb-4 scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-gray-800">
                {/* Logo và header section */}
                <div className="flex-shrink-0">
                  <button
                    className="hidden md:block text-white mb-4"
                    onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                  >
                    <FiMenu className="text-xl" />
                  </button>
                </div>

                {/* Menu items */}
                <div className="flex-1">
                  <ul className="flex flex-col space-y-4 mb-8 md:mb-0">
                    {/* Post Creation Group */}
                    <div className="pb-2">
                      <button
                        onClick={() => toggleGroup("posts")}
                        className={`w-full text-white rounded flex items-center ${
                          isSidebarCollapsed
                            ? "justify-center px-3 py-4"
                            : "justify-between p-2"
                        } hover:bg-gray-700`}
                      >
                        <span className={isSidebarCollapsed ? "hidden" : ""}>
                          Bài viết
                        </span>
                        {!isSidebarCollapsed &&
                          (expandedGroups.posts ? (
                            <FiChevronDown />
                          ) : (
                            <FiChevronRight />
                          ))}
                      </button>
                      {expandedGroups.posts && (
                        <div className="ml-4 mt-2 space-y-2">
                          <li>
                            <Link
                              to="/ai-price"
                              className={`text-white rounded flex items-center ${
                                isSidebarCollapsed
                                  ? "justify-center px-1 py-3"
                                  : "justify-start p-2"
                              } ${
                                location.pathname === "/ai-price"
                                  ? "bg-gray-700"
                                  : "hover:bg-gray-700"
                              }`}
                              onClick={() => setIsSidebarOpen(false)}
                            >
                              <FiDollarSign
                                className={`text-2xl ${
                                  !isSidebarCollapsed ? "mr-2" : ""
                                }`}
                              />
                              <span
                                className={`${
                                  isSidebarCollapsed ? "hidden" : ""
                                }`}
                              >
                                Định giá BDS
                              </span>
                            </Link>
                          </li>
                          <li>
                            <Link
                              to="/ai-rental-price"
                              className={`text-white rounded flex items-center ${
                                isSidebarCollapsed
                                  ? "justify-center px-1 py-3"
                                  : "justify-start p-2"
                              } ${
                                location.pathname === "/ai-rental-price"
                                  ? "bg-gray-700"
                                  : "hover:bg-gray-700"
                              }`}
                              onClick={() => setIsSidebarOpen(false)}
                            >
                              <FiHome
                                className={`text-2xl ${
                                  !isSidebarCollapsed ? "mr-2" : ""
                                }`}
                              />
                              <span
                                className={`${
                                  isSidebarCollapsed ? "hidden" : ""
                                }`}
                              >
                                Định giá thuê BDS
                              </span>
                            </Link>
                          </li>
                          <li>
                            <Link
                              to="/price-history"
                              className={`text-white rounded flex items-center ${
                                isSidebarCollapsed
                                  ? "justify-center px-1 py-3"
                                  : "justify-start p-2"
                              } ${
                                location.pathname === "/price-history"
                                  ? "bg-gray-700"
                                  : "hover:bg-gray-700"
                              }`}
                              onClick={() => setIsSidebarOpen(false)}
                            >
                              <FiBookmark
                                className={`text-2xl ${
                                  !isSidebarCollapsed ? "mr-2" : ""
                                }`}
                              />
                              <span
                                className={`${
                                  isSidebarCollapsed ? "hidden" : ""
                                }`}
                              >
                                Lịch sử định giá
                              </span>
                            </Link>
                          </li>
                          {/* <li>
                            <Link
                              to="/manus-test"
                              className={`text-white rounded flex items-center ${
                                isSidebarCollapsed
                                  ? "justify-center px-1 py-3"
                                  : "justify-start p-2"
                              } ${
                                location.pathname === "/manus-test"
                                  ? "bg-gray-700"
                                  : "hover:bg-gray-700"
                              }`}
                              onClick={() => setIsSidebarOpen(false)}
                            >
                              <FiActivity
                                className={`text-2xl ${
                                  !isSidebarCollapsed ? "mr-2" : ""
                                }`}
                              />
                              <span
                                className={`${
                                  isSidebarCollapsed ? "hidden" : ""
                                }`}
                              >
                                🧪 Test AIPrice Flow
                              </span>
                            </Link>
                          </li> */}
                        </div>
                      )}
                    </div>

                    {/* Settings Group */}
                    <div className="py-2 border-t border-gray-700">
                      <button
                        onClick={() => toggleGroup("settings")}
                        className={`w-full text-white rounded flex items-center ${
                          isSidebarCollapsed
                            ? "justify-center px-3 py-4"
                            : "justify-between p-2"
                        } hover:bg-gray-700`}
                      >
                        <span className={isSidebarCollapsed ? "hidden" : ""}>
                          Cài đặt
                        </span>
                        {!isSidebarCollapsed &&
                          (expandedGroups.settings ? (
                            <FiChevronDown />
                          ) : (
                            <FiChevronRight />
                          ))}
                      </button>
                      {expandedGroups.settings && (
                        <div className="ml-4 mt-2 space-y-2">
                          <li>
                            <Link
                              to="/profile"
                              className={`text-white rounded flex items-center ${
                                isSidebarCollapsed
                                  ? "justify-center px-1 py-3"
                                  : "justify-start p-2"
                              } ${
                                location.pathname === "/profile"
                                  ? "bg-gray-700"
                                  : "hover:bg-gray-700"
                              }`}
                              onClick={() => setIsSidebarOpen(false)}
                            >
                              <FiUser
                                className={`text-2xl ${
                                  !isSidebarCollapsed ? "mr-2" : ""
                                }`}
                              />
                              <span
                                className={isSidebarCollapsed ? "hidden" : ""}
                              >
                                Thiết lập thông tin
                              </span>
                            </Link>
                          </li>
                          <li>
                            <a
                              href="/landing-page#contact"
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`text-white rounded flex items-center ${
                                isSidebarCollapsed
                                  ? "justify-center px-1 py-3"
                                  : "justify-start p-2"
                              } hover:bg-gray-700`}
                              onClick={() => setIsSidebarOpen(false)}
                            >
                              <FiMessageCircle
                                className={`text-2xl ${
                                  !isSidebarCollapsed ? "mr-2" : ""
                                }`}
                              />
                              <span
                                className={isSidebarCollapsed ? "hidden" : ""}
                              >
                                Hỗ trợ
                              </span>
                            </a>
                          </li>
                          <li>
                            <Link
                              to="/policy"
                              className={`text-white rounded flex items-center ${
                                isSidebarCollapsed
                                  ? "justify-center px-1 py-3"
                                  : "justify-start p-2"
                              } ${
                                location.pathname === "/terms"
                                  ? "bg-gray-700"
                                  : "hover:bg-gray-700"
                              }`}
                              onClick={() => setIsSidebarOpen(false)}
                            >
                              <FiFileText
                                className={`text-2xl ${
                                  !isSidebarCollapsed ? "mr-2" : ""
                                }`}
                              />
                              <span
                                className={isSidebarCollapsed ? "hidden" : ""}
                              >
                                Điều khoản dịch vụ
                              </span>
                            </Link>
                          </li>
                          <li>
                            <LanguageSelector
                              isSidebar={true}
                              isSidebarCollapsed={isSidebarCollapsed}
                            />
                          </li>
                          <li>
                            <button
                              onClick={() => {
                                handleLogout();
                                setIsSidebarOpen(false);
                              }}
                              className={`text-white rounded flex items-center w-full ${
                                isSidebarCollapsed
                                  ? "justify-center px-1 py-3"
                                  : "justify-start p-2"
                              } hover:bg-gray-700`}
                            >
                              <FiLogOut
                                className={`text-2xl ${
                                  !isSidebarCollapsed ? "mr-2" : ""
                                }`}
                              />
                              <span
                                className={isSidebarCollapsed ? "hidden" : ""}
                              >
                                Đăng xuất
                              </span>
                            </button>
                          </li>
                        </div>
                      )}
                    </div>
                    {/* Feedback Button - Placed after Settings */}
                    <li className="py-2 border-t border-gray-700">
                      <button
                        onClick={() => setShowFeedbackForm(true)}
                        className={`text-white rounded flex items-center w-full ${
                          isSidebarCollapsed
                            ? "justify-center px-1 py-3"
                            : "justify-start p-2"
                        } hover:bg-gray-700`}
                      >
                        <FiMessageSquare
                          className={`text-2xl ${
                            !isSidebarCollapsed ? "mr-2" : ""
                          }`}
                        />
                        <span className={isSidebarCollapsed ? "hidden" : ""}>
                          Gửi Phản Hồi
                        </span>
                      </button>
                    </li>

                    {/* Admin Menu - chỉ hiển thị nếu là admin */}
                    {localStorage.getItem("isAdmin") === "true" && (
                      <div className="py-2 border-t border-gray-700">
                        <button
                          onClick={() => toggleGroup("admin")}
                          className={`w-full text-white rounded flex items-center ${
                            isSidebarCollapsed
                              ? "justify-center px-3 py-4"
                              : "justify-between p-2"
                          } hover:bg-gray-700`}
                        >
                          <div className="flex items-center">
                            <FiShield
                              className={`text-2xl ${
                                !isSidebarCollapsed ? "mr-2" : ""
                              }`}
                            />
                            <span
                              className={isSidebarCollapsed ? "hidden" : ""}
                            >
                              Quản trị hệ thống
                            </span>
                          </div>
                          {!isSidebarCollapsed &&
                            (expandedGroups.admin ? (
                              <FiChevronDown />
                            ) : (
                              <FiChevronRight />
                            ))}
                        </button>
                        {expandedGroups.admin && (
                          <div className="ml-4 mt-2 space-y-2">
                            <li>
                              <Link
                                to="/admin-dashboard"
                                className={`text-white rounded flex items-center ${
                                  isSidebarCollapsed
                                    ? "justify-center px-1 py-3"
                                    : "justify-start p-2"
                                } ${
                                  location.pathname === "/admin-dashboard"
                                    ? "bg-gray-700"
                                    : "hover:bg-gray-700"
                                }`}
                                onClick={() => setIsSidebarOpen(false)}
                              >
                                <FiDatabase
                                  className={`text-2xl ${
                                    !isSidebarCollapsed ? "mr-2" : ""
                                  }`}
                                />
                                <span
                                  className={isSidebarCollapsed ? "hidden" : ""}
                                >
                                  Bảng điều khiển
                                </span>
                              </Link>
                            </li>
                            <li>
                              <Link
                                to="/admin-users"
                                className={`text-white rounded flex items-center ${
                                  isSidebarCollapsed
                                    ? "justify-center px-1 py-3"
                                    : "justify-start p-2"
                                } ${
                                  location.pathname === "/admin-users"
                                    ? "bg-gray-700"
                                    : "hover:bg-gray-700"
                                }`}
                                onClick={() => setIsSidebarOpen(false)}
                              >
                                <FiUsers
                                  className={`text-2xl ${
                                    !isSidebarCollapsed ? "mr-2" : ""
                                  }`}
                                />
                                <span
                                  className={isSidebarCollapsed ? "hidden" : ""}
                                >
                                  Quản lý người dùng
                                </span>
                              </Link>
                            </li>
                            <li>
                              <Link
                                to="/admin-errors"
                                className={`text-white rounded flex items-center ${
                                  isSidebarCollapsed
                                    ? "justify-center px-1 py-3"
                                    : "justify-start p-2"
                                } ${
                                  location.pathname === "/admin-errors"
                                    ? "bg-gray-700"
                                    : "hover:bg-gray-700"
                                }`}
                                onClick={() => setIsSidebarOpen(false)}
                              >
                                <FiActivity
                                  className={`text-2xl ${
                                    !isSidebarCollapsed ? "mr-2" : ""
                                  }`}
                                />
                                <span
                                  className={isSidebarCollapsed ? "hidden" : ""}
                                >
                                  Quản lý lỗi
                                </span>
                              </Link>
                            </li>
                            <li>
                              <Link
                                to="/admin-news"
                                className={`text-white rounded flex items-center ${
                                  isSidebarCollapsed
                                    ? "justify-center px-1 py-3"
                                    : "justify-start p-2"
                                } ${
                                  location.pathname === "/admin-news"
                                    ? "bg-gray-700"
                                    : "hover:bg-gray-700"
                                }`}
                                onClick={() => setIsSidebarOpen(false)}
                              >
                                <FiFileText
                                  className={`text-2xl ${
                                    !isSidebarCollapsed ? "mr-2" : ""
                                  }`}
                                />
                                <span
                                  className={isSidebarCollapsed ? "hidden" : ""}
                                >
                                  Quản lý tin tức
                                </span>
                              </Link>
                            </li>
                            <li>
                              <Link
                                to="/admin-usage-management"
                                className={`text-white rounded flex items-center ${
                                  isSidebarCollapsed
                                    ? "justify-center px-1 py-3"
                                    : "justify-start p-2"
                                } ${
                                  location.pathname ===
                                  "/admin-usage-management"
                                    ? "bg-gray-700"
                                    : "hover:bg-gray-700"
                                }`}
                                onClick={() => setIsSidebarOpen(false)}
                              >
                                <FiSettings
                                  className={`text-2xl ${
                                    !isSidebarCollapsed ? "mr-2" : ""
                                  }`}
                                />
                                <span
                                  className={isSidebarCollapsed ? "hidden" : ""}
                                >
                                  Quản lý giới hạn
                                </span>
                              </Link>
                            </li>
                            <li>
                              <Link
                                to="/admin-usage-analytics"
                                className={`text-white rounded flex items-center ${
                                  isSidebarCollapsed
                                    ? "justify-center px-1 py-3"
                                    : "justify-start p-2"
                                } ${
                                  location.pathname === "/admin-usage-analytics"
                                    ? "bg-gray-700"
                                    : "hover:bg-gray-700"
                                }`}
                                onClick={() => setIsSidebarOpen(false)}
                              >
                                <FiActivity
                                  className={`text-2xl ${
                                    !isSidebarCollapsed ? "mr-2" : ""
                                  }`}
                                />
                                <span
                                  className={isSidebarCollapsed ? "hidden" : ""}
                                >
                                  Phân tích sử dụng
                                </span>
                              </Link>
                            </li>
                            <li>
                              <Link
                                to="/admin-manus-keys"
                                className={`text-white rounded flex items-center ${
                                  isSidebarCollapsed
                                    ? "justify-center px-1 py-3"
                                    : "justify-start p-2"
                                } ${
                                  location.pathname === "/admin-manus-keys"
                                    ? "bg-gray-700"
                                    : "hover:bg-gray-700"
                                }`}
                                onClick={() => setIsSidebarOpen(false)}
                              >
                                <FiKey
                                  className={`text-2xl ${
                                    !isSidebarCollapsed ? "mr-2" : ""
                                  }`}
                                />
                                <span
                                  className={isSidebarCollapsed ? "hidden" : ""}
                                >
                                  Quản lý API Keys
                                </span>
                              </Link>
                            </li>
                            <li>
                              <Link
                                to="/payment/test"
                                className={`text-white rounded flex items-center ${
                                  isSidebarCollapsed
                                    ? "justify-center px-1 py-3"
                                    : "justify-start p-2"
                                } ${
                                  location.pathname === "/payment/test"
                                    ? "bg-gray-700"
                                    : "hover:bg-gray-700"
                                }`}
                                onClick={() => setIsSidebarOpen(false)}
                              >
                                <FiCreditCard
                                  className={`text-2xl ${
                                    !isSidebarCollapsed ? "mr-2" : ""
                                  }`}
                                />
                                <span
                                  className={isSidebarCollapsed ? "hidden" : ""}
                                >
                                  Test Payment Flow
                                </span>
                              </Link>
                            </li>
                          </div>
                        )}
                      </div>
                    )}
                  </ul>
                </div>
              </div>
            </div>
          </nav>

          {/* Main Content */}
          <div className="flex-1 p-0 md:p-4 md:mt-0 mt-10 min-h-screen">
            {" "}
            {/* Added min-h-screen */}
            <Routes>
              {/* Public routes */}
              <Route path="/" element={<LandingPage2 />} />
              <Route path="/landing-page" element={<LandingPage2 />} />
              <Route path="/admin-dashboard" element={<AdminDashboard />} />
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/admin-manus-keys" element={<AdminManusKeyManager />} />

              {/* Protected routes wrapped in MaintenanceLayout */}
              <Route element={<MaintenanceLayout />}>
                {/* Route path="/login" removed as it's likely unreachable when logged in */}
                <Route path="/profile" element={<ProfileScreen />} />
                {/* Route path="/pricing" removed, handled above */}
                {/* Route path="/policy" removed, handled above */}
                {/* Route path="/terms" removed, handled above */}
                {/* News routes removed, handled above */}
                {/* Admin News route removed, handled above */}
                <Route path="/ai-price" element={<AIPrice />} />
                <Route path="/ai-rental-price" element={<AIRentalPrice />} />
                <Route path="/price-history" element={<PriceHistoryScreen />} />
                {/* Consider moving Admin routes here if they need protection */}
              </Route>

              {/* Payment routes */}
              <Route path="/payment/success" element={<PaymentSuccess />} />
              <Route path="/payment/cancel" element={<PaymentCancel />} />
              <Route path="/payment/test" element={<PaymentFlowTest />} />

              <Route path="*" element={<Navigate to="/" />} />
            </Routes>
          </div>
          {/* Feedback Form Modal */}
          {showFeedbackForm && (
            <div
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1000, // Ensure it's above other content
                padding: "20px", // Add padding for smaller screens
                overflowY: "auto", // Allow scrolling if form is tall
              }}
            >
              <FeedbackForm onClose={() => setShowFeedbackForm(false)} />
            </div>
          )}
        </>
      ) : (
        <div className="flex flex-1 justify-center items-center">
          <Routes>
            <Route path="/landing-page" element={<LandingPage2 />} />
            <Route path="/pricing" element={<PricingScreen />} />
            <Route path="/login" element={<LoginGoogle />} />
            <Route path="/admin-news" element={<AdminNewsGenerator />} />

            {/* Payment routes */}
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />
            <Route path="/payment/test" element={<PaymentFlowTest />} />

            <Route path="/" element={<LandingPage2 />} />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </div>
      )}
    </div>
  );
};

const App = () => {
  return (
    <HelmetProvider>
      <MaintenanceProvider>
        <MetaPixelPageTracker />
        <AppContent />
      </MaintenanceProvider>
    </HelmetProvider>
  );
};

export default App;
